import type { WorkflowDefinition } from "../types";

/**
 * W2 — Start tracking visibility over time.
 *
 * The canonical activation flow, and the workflow the design-lab prototype was
 * built against (it was named `visibility-baseline` there; the content is W2's,
 * so it takes the W2 id here and W1 gets its own file).
 *
 * `shared`: this is *the property's* checklist. Anyone on the team continues
 * the same run, because the state it observes is shared property state.
 */
export const visibilityTrackingWorkflow: WorkflowDefinition = {
	id: "visibility-tracking",
	wNumber: 2,
	title: "Track your brand's AI visibility over time",
	outcome:
		"Track visibility every day, with a goal attached, so you can show what moved and why.",
	audiences: ["agencies", "pr", "inhouse"],
	uses: ["AI Reports", "Prompts", "Competitors", "Projects"],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	// A focal brand is what step one measures: the report describes the
	// subject and ranks it against the competitors named around it. A CATEGORY
	// has none, and its brands come out of the report rather than into it, so
	// it gets W32 instead: same five checkpoints, market wording.
	isApplicable: (mode) => mode.showProjects && mode.hasFocalSubject,
	articleSlug: "how-to-track-ai-visibility-over-time",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "run-report",
			title: "Start an AI Visibility Report",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've completed one report.",
			why: "The report is your starting point. It shows how AI describes the brand and how it compares with rivals.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Start a report and wait 4 to 15 minutes",
				"Skim the summary, the platform breakdown and the recommendations",
				"Share the branded link or PDF with the people who need it",
			],
			complete: { kind: "count", key: "completedReportCount", gte: 1 },
			spend: { kind: "aiReport" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Completed reports",
							value: state.property.completedReportCount,
							target: 1,
						}
					: null,
		},
		{
			id: "expand-prompts",
			title: "Add the prompts your buyers would ask",
			page: "Prompts",
			route: "/discovery-queries",
			criterion:
				"You've added 15 or more prompts across all three buying stages.",
			why: "Add prompts that cover your customer segments, the problems the product solves, and each stage of the buyer's journey.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Generate prompts from the frameworks that fit the brand",
				"Import any prompt lists you already have",
				"Fill the red cells in the Coverage Matrix and tag your prompts",
			],
			// Proxy for the coverage score: computing the real one embeds every
			// prompt through OpenAI, which has no business in a state loader.
			// The step panel hydrates the true score lazily.
			complete: {
				kind: "all",
				of: [
					{ kind: "count", key: "activePromptCount", gte: 15 },
					{ kind: "count", key: "promptStageCount", gte: 3 },
				],
			},
			enrichment: "coverageScore",
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active prompts",
							value: state.property.activePromptCount,
							target: 15,
							caption: `${state.property.promptStageCount} of 3 buying stages covered`,
						}
					: null,
		},
		{
			id: "revise-competitors",
			title: "Add the competitors that matter",
			page: "Competitors",
			route: "/competitors",
			criterion: "You've added at least four competitors.",
			why: "Share of voice is measured against this list. It has to name the brands that really compete for the same answers.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Remove suggested competitors that do not matter",
				"Add the ones named in the brand's own reporting",
				"Add the other names and domains each one uses",
			],
			complete: { kind: "count", key: "competitorCount", gte: 4 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Competitors tracked",
							value: state.property.competitorCount,
							target: 4,
						}
					: null,
		},
		{
			id: "create-project",
			title: "Set up a project to track prompts daily",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set up one active project.",
			why: "A project is what puts your prompts on a daily schedule. It also holds your goals and shows the margin of error.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Pick the type: SEO, PR or General",
				"Choose your prompts, then check the Data Accuracy card",
				"Set the start date and any goals",
			],
			complete: { kind: "count", key: "activeProjectCount", gte: 1 },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Active project",
									done: state.property.activeProjectCount > 0,
								},
								{
									label: "Prompts assigned",
									done: state.property.activePromptCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "connect-goal",
			title: "Connect a goal to the project",
			page: "Projects",
			route: "/projects",
			criterion: "You've added a goal to an active project.",
			why: "A goal ties the work you are doing to the visibility you are measuring. An SEO goal points at a group of your pages. A PR goal points at a coverage group, a publisher, an article or an author.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"For SEO work, import your pages, group the ones the work is about, and point a goal at that group",
				"For PR work, point a goal at a coverage group, a publisher, an article or an author",
				"Open the project and add the goal from the Goals card",
			],
			complete: {
				kind: "count",
				key: "activeProjectGoalCount",
				gte: 1,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Goals on active projects",
							value: state.property.activeProjectGoalCount,
							target: 1,
						}
					: null,
		},
	],
};
